// ════════════════════════════════════════════════════════════
// Sheet ECR — drawer latéral / supérieur / inférieur
// ════════════════════════════════════════════════════════════
// Sides : right (défaut), left, top, bottom.
//   • right/left/top → Radix Dialog + animations slide Tailwind
//   • bottom → Vaul Drawer (cf. sheet-bottom.tsx pour SheetBottomContent)
//
// Anatomie : Sheet · SheetTrigger · SheetContent · SheetHeader ·
// SheetTitle · SheetDescription · SheetFooter · SheetClose · SheetOverlay.
// ════════════════════════════════════════════════════════════
import * as React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { Drawer as VaulDrawer } from 'vaul';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { useModalStack, backdropOpacityClass } from '@/components/ui/dialog/hooks/use-modal-stack';
import {
  usePreventClose,
  type PreventCloseFn,
} from '@/components/ui/dialog/hooks/use-prevent-close';

export type SheetSide = 'right' | 'left' | 'top' | 'bottom';
export type SheetSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

// ─── Contexte ────────────────────────────────────────────────

interface SheetContextValue {
  side: SheetSide;
  size: SheetSize;
  snapPoints?: (number | string)[];
  dragHandle: boolean;
  shakeControls: ReturnType<typeof usePreventClose>['shakeControls'];
  dismissibleEscape: boolean;
  dismissibleOverlay: boolean;
  closeButton: boolean;
  requestClose: () => void;
  hasPreventClose: boolean;
}

const SheetContext = React.createContext<SheetContextValue | null>(null);
function useSheetContext(): SheetContextValue {
  const ctx = React.useContext(SheetContext);
  if (!ctx) {
    throw new Error("Sheet sous-composants doivent être enfants d'un <Sheet> root.");
  }
  return ctx;
}

// ─── Root ────────────────────────────────────────────────────

export interface SheetProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: SheetSide;
  size?: SheetSize;
  /** Snap points (bottom only). Ex. [0.4, 0.8, 1] ou ['200px', '400px']. */
  snapPoints?: (number | string)[];
  dragHandle?: boolean;
  dismissibleEscape?: boolean;
  dismissibleOverlay?: boolean;
  closeButton?: boolean;
  preventClose?: PreventCloseFn;
  children: React.ReactNode;
}

function Sheet({
  open,
  defaultOpen,
  onOpenChange,
  side = 'right',
  size = 'md',
  snapPoints,
  dragHandle,
  dismissibleEscape = true,
  dismissibleOverlay = true,
  closeButton = true,
  preventClose,
  children,
}: SheetProps) {
  const { handleOpenChange, requestClose, shakeControls, hasPreventClose } = usePreventClose({
    preventClose,
    onOpenChange,
  });
  const effectiveDragHandle = dragHandle ?? side === 'bottom';

  const ctx = React.useMemo<SheetContextValue>(
    () => ({
      side,
      size,
      snapPoints,
      dragHandle: effectiveDragHandle,
      shakeControls,
      dismissibleEscape,
      dismissibleOverlay,
      closeButton,
      requestClose,
      hasPreventClose,
    }),
    [
      side,
      size,
      snapPoints,
      effectiveDragHandle,
      shakeControls,
      dismissibleEscape,
      dismissibleOverlay,
      closeButton,
      requestClose,
      hasPreventClose,
    ],
  );

  // Bottom → Vaul
  if (side === 'bottom') {
    return (
      <SheetContext.Provider value={ctx}>
        <VaulDrawer.Root
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={handleOpenChange}
          snapPoints={snapPoints}
          dismissible={dismissibleOverlay}
        >
          {children}
        </VaulDrawer.Root>
      </SheetContext.Provider>
    );
  }

  // right/left/top → Radix Dialog
  return (
    <SheetContext.Provider value={ctx}>
      <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
        {children}
      </RadixDialog.Root>
    </SheetContext.Provider>
  );
}

// ─── Trigger ─────────────────────────────────────────────────

const SheetTrigger = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Trigger>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Trigger>
>((props, ref) => {
  const { side } = useSheetContext();
  if (side === 'bottom') return <VaulDrawer.Trigger ref={ref} {...props} />;
  return <RadixDialog.Trigger ref={ref} {...props} />;
});
SheetTrigger.displayName = 'SheetTrigger';

// ─── Close ───────────────────────────────────────────────────

const SheetClose = React.forwardRef<
  React.ElementRef<typeof RadixDialog.Close>,
  React.ComponentPropsWithoutRef<typeof RadixDialog.Close>
>((props, ref) => {
  const { side } = useSheetContext();
  if (side === 'bottom') return <VaulDrawer.Close ref={ref} {...props} />;
  return <RadixDialog.Close ref={ref} {...props} />;
});
SheetClose.displayName = 'SheetClose';

// ─── Sizes (par side) ───────────────────────────────────────
// right/left : largeur. top : hauteur. bottom : auto (Vaul gère).

const sideSizeClass: Record<Exclude<SheetSide, 'bottom'>, Record<SheetSize, string>> = {
  right: {
    sm: 'w-[320px] max-w-[90vw]',
    md: 'w-[480px] max-w-[90vw]',
    lg: 'w-[640px] max-w-[90vw]',
    xl: 'w-[800px] max-w-[90vw]',
    full: 'w-screen',
  },
  left: {
    sm: 'w-[320px] max-w-[90vw]',
    md: 'w-[480px] max-w-[90vw]',
    lg: 'w-[640px] max-w-[90vw]',
    xl: 'w-[800px] max-w-[90vw]',
    full: 'w-screen',
  },
  top: {
    sm: 'h-[25vh]',
    md: 'h-[50vh]',
    lg: 'h-[75vh]',
    xl: 'h-[90vh]',
    full: 'h-screen',
  },
};

const sheetContentVariants = cva(
  'fixed z-50 flex flex-col gap-0 bg-card text-card-foreground shadow-2xl outline-none focus:outline-none ' +
    'data-[state=open]:animate-in data-[state=closed]:animate-out duration-300 ease-out ' +
    'motion-reduce:animate-none motion-reduce:duration-0',
  {
    variants: {
      side: {
        right:
          'inset-y-0 right-0 border-l border-border data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
        left: 'inset-y-0 left-0 border-r border-border data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
        top: 'inset-x-0 top-0 border-b border-border data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
      },
    },
    defaultVariants: { side: 'right' },
  },
);

// ─── Content ─────────────────────────────────────────────────

export interface SheetContentProps
  extends
    Omit<React.ComponentPropsWithoutRef<typeof RadixDialog.Content>, 'onOpenAutoFocus'>,
    VariantProps<typeof sheetContentVariants> {
  disableAutoFocus?: boolean;
}

const SheetContent = React.forwardRef<HTMLDivElement, SheetContentProps>(
  ({ className, children, disableAutoFocus = false, ...props }, ref) => {
    const ctx = useSheetContext();
    const stack = useModalStack(true);
    const showOpaqueBackdrop = stack.index === 0;

    // Bottom → Vaul
    if (ctx.side === 'bottom') {
      return (
        <VaulDrawer.Portal>
          <VaulDrawer.Overlay
            className={cn(
              'fixed inset-0 z-50',
              showOpaqueBackdrop ? backdropOpacityClass(stack.depth) : 'bg-transparent',
            )}
          />
          <VaulDrawer.Content
            ref={ref}
            className={cn(
              'bg-card text-card-foreground fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto max-h-[90vh] flex-col rounded-t-2xl shadow-2xl outline-none',
              className,
            )}
            onEscapeKeyDown={(e) => {
              if (!ctx.dismissibleEscape) {
                e.preventDefault();
                return;
              }
              if (ctx.hasPreventClose) {
                e.preventDefault();
                ctx.requestClose();
              }
            }}
            onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
            {...props}
          >
            {ctx.dragHandle ? (
              // VaulDrawer.Handle = drag handle nativement bindée aux
              // gestures Vaul (drag-down/up + snap points). Permet aussi
              // de drag depuis cette zone même quand le body intérieur
              // a overflow-y-auto (scroll lock 500 ms par défaut).
              <VaulDrawer.Handle
                data-testid="vaul-drawer-handle"
                className="bg-muted-foreground/30 mx-auto mt-2 h-1 w-9 shrink-0 rounded-full"
              />
            ) : null}
            <div className="relative flex max-h-[calc(90vh-2rem)] flex-col overflow-hidden">
              {children}
              {ctx.closeButton ? (
                <VaulDrawer.Close
                  className={cn(
                    'text-muted-foreground absolute right-4 top-3 rounded-md p-1.5 transition-opacity hover:opacity-100',
                    'focus:ring-ring opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2',
                    'motion-reduce:transition-none',
                  )}
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </VaulDrawer.Close>
              ) : null}
            </div>
          </VaulDrawer.Content>
        </VaulDrawer.Portal>
      );
    }

    const side = ctx.side;
    const sizeCls = sideSizeClass[side][ctx.size];

    return (
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className={cn(
            'fixed inset-0 z-50 transition-colors duration-200',
            showOpaqueBackdrop ? backdropOpacityClass(stack.depth) : 'bg-transparent',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'motion-reduce:animate-none motion-reduce:duration-0',
          )}
        />
        <RadixDialog.Content
          ref={ref}
          asChild
          onEscapeKeyDown={(e) => {
            if (!ctx.dismissibleEscape) {
              e.preventDefault();
              return;
            }
            if (ctx.hasPreventClose) {
              e.preventDefault();
              ctx.requestClose();
            }
          }}
          onPointerDownOutside={(e) => {
            if (!ctx.dismissibleOverlay) {
              e.preventDefault();
              return;
            }
            if (ctx.hasPreventClose) {
              e.preventDefault();
              ctx.requestClose();
            }
          }}
          onInteractOutside={(e) => {
            if (!ctx.dismissibleOverlay) {
              e.preventDefault();
              return;
            }
            if (ctx.hasPreventClose) {
              e.preventDefault();
              ctx.requestClose();
            }
          }}
          onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
          {...props}
        >
          <motion.div
            animate={ctx.shakeControls}
            className={cn(sheetContentVariants({ side }), sizeCls, className)}
          >
            {children}
            {ctx.closeButton ? (
              <RadixDialog.Close
                className={cn(
                  'text-muted-foreground absolute right-4 top-4 rounded-md p-1.5 transition-opacity hover:opacity-100',
                  'focus:ring-ring opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2',
                  'motion-reduce:transition-none',
                )}
                aria-label="Fermer"
              >
                <X className="h-4 w-4" />
              </RadixDialog.Close>
            ) : null}
          </motion.div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    );
  },
);
SheetContent.displayName = 'SheetContent';

// ─── Header / Body / Footer ──────────────────────────────────

// px-4 sur très petit viewport (<420 px), px-6 ailleurs (cf. Dialog).
const SHEET_PADDING_X = 'px-4 sm:px-6';

const SheetHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-border flex flex-col gap-1.5 border-b py-5 text-left',
        SHEET_PADDING_X,
        className,
      )}
      {...props}
    />
  ),
);
SheetHeader.displayName = 'SheetHeader';

const SheetBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex-1 overflow-y-auto py-5', SHEET_PADDING_X, className)}
      {...props}
    />
  ),
);
SheetBody.displayName = 'SheetBody';

const SheetFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'border-border flex flex-col-reverse gap-2 border-t py-4',
        SHEET_PADDING_X,
        'min-[420px]:flex-row min-[420px]:justify-end',
        '[&>button]:w-full min-[420px]:[&>button]:w-auto',
        className,
      )}
      {...props}
    />
  ),
);
SheetFooter.displayName = 'SheetFooter';

// ─── Title / Description ─────────────────────────────────────

const SheetTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => {
    const { side } = useSheetContext();
    const Comp = side === 'bottom' ? VaulDrawer.Title : RadixDialog.Title;
    return (
      <Comp
        ref={ref}
        className={cn('text-base font-semibold leading-tight tracking-tight', className)}
        {...props}
      />
    );
  },
);
SheetTitle.displayName = 'SheetTitle';

const SheetDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { side } = useSheetContext();
  const Comp = side === 'bottom' ? VaulDrawer.Description : RadixDialog.Description;
  return <Comp ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />;
});
SheetDescription.displayName = 'SheetDescription';

export {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetClose,
};
